// ════════════════════════════════════════════════════════════════════════════
//  Udisys Gestor Web  —  pages/OpenFinance.tsx
//  Integração Open Finance via Pluggy: conexões (Items), contas descobertas,
//  vínculo conta↔ContaCorrente e sincronização de extrato → conciliação.
//  O consentimento é feito pelo widget Pluggy Connect (carregado por CDN).
// ════════════════════════════════════════════════════════════════════════════

import { useEffect, useState } from 'react';
import {
  Landmark, Settings, Plug, RefreshCw, Loader2, CheckCircle2, XCircle,
  Trash2, Link2, Download, Clock,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { apiClient, getApiErrorMessage } from '@/lib/apiClient';
import { toast } from '@/components/ui/sonner';

// ── Pluggy Connect (widget via CDN) ─────────────────────────────────────────

const PLUGGY_CONNECT_CDN = 'https://cdn.pluggy.ai/pluggy-connect/latest/pluggy-connect.js';

interface PluggyConnectOptions {
  connectToken: string;
  includeSandbox?: boolean;
  updateItem?: string;
  onSuccess?: (data: { item: { id: string } }) => void;
  onError?: (err: unknown) => void;
  onClose?: () => void;
}
type PluggyConnectCtor = new (opts: PluggyConnectOptions) => { init: () => void };
declare global {
  interface Window { PluggyConnect?: PluggyConnectCtor }
}

/** Carrega o script do widget uma única vez. Resolve com o construtor ou rejeita. */
function carregarPluggyConnect(): Promise<PluggyConnectCtor> {
  return new Promise((resolve, reject) => {
    if (window.PluggyConnect) { resolve(window.PluggyConnect); return; }
    const existente = document.querySelector<HTMLScriptElement>(`script[src="${PLUGGY_CONNECT_CDN}"]`);
    if (existente) {
      existente.addEventListener('load', () => window.PluggyConnect ? resolve(window.PluggyConnect) : reject(new Error('PluggyConnect indisponível.')));
      existente.addEventListener('error', () => reject(new Error('Falha ao carregar o widget Pluggy.')));
      return;
    }
    const s = document.createElement('script');
    s.src = PLUGGY_CONNECT_CDN;
    s.async = true;
    s.onload = () => window.PluggyConnect ? resolve(window.PluggyConnect) : reject(new Error('PluggyConnect indisponível.'));
    s.onerror = () => reject(new Error('Falha ao carregar o widget Pluggy.'));
    document.body.appendChild(s);
  });
}

// ── Tipos ───────────────────────────────────────────────────────────────────

interface ConfigOF {
  clientId: string | null;
  webhookUrl: string | null;
  ambiente: number;
  ativo: boolean;
  segredoCadastrado: boolean;
}

interface ContaOF {
  id: number;
  accountId: string;
  tipo: string | null;
  subtipo: string | null;
  nome: string | null;
  numero: string | null;
  saldo: number | null;
  moeda: string | null;
  contaCorrenteId: number | null;
}

interface ConexaoOF {
  id: number;
  itemId: string;
  conectorId: number | null;
  instituicao: string | null;
  status: string | null;
  executionStatus: string | null;
  consentExpiraEm: string | null;
  ultimaSincronizacao: string | null;
  contas: ContaOF[];
}

interface ContaCorrente { id: number; descricao: string; bancoId: number | null }

// ── Helpers ───────────────────────────────────────────────────────────────

const fmt = (n: number | null | undefined) =>
  (n ?? 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });

const statusBadge = (status: string | null) => {
  switch ((status ?? '').toUpperCase()) {
    case 'UPDATED':           return <Badge className="bg-green-100 text-green-800 border-green-200">Atualizado</Badge>;
    case 'UPDATING':          return <Badge className="bg-blue-100 text-blue-800 border-blue-200">Atualizando</Badge>;
    case 'WAITING_USER_INPUT':return <Badge className="bg-yellow-100 text-yellow-800 border-yellow-200">Aguardando usuário</Badge>;
    case 'LOGIN_ERROR':       return <Badge variant="destructive">Erro de login</Badge>;
    case 'OUTDATED':          return <Badge variant="destructive">Desatualizado</Badge>;
    default:                  return <Badge variant="outline">{status ?? '—'}</Badge>;
  }
};

// ── Tab: Configuração ─────────────────────────────────────────────────────

function TabConfig({ onSalvo }: { onSalvo: () => void }) {
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [segredoCadastrado, setSegredoCadastrado] = useState(false);
  const [form, setForm] = useState({
    clientId: '', clientSecret: '', webhookUrl: '', ambiente: '1', ativo: true,
  });

  const carregar = async () => {
    setLoading(true);
    try {
      const { data } = await apiClient.get<ConfigOF>('/api/open-finance/config');
      setForm({
        clientId:   data.clientId ?? '',
        clientSecret: '',
        webhookUrl: data.webhookUrl ?? '',
        ambiente:   String(data.ambiente ?? 1),
        ativo:      data.ativo,
      });
      setSegredoCadastrado(data.segredoCadastrado);
    } catch (e) { toast.error('Erro ao carregar', { description: getApiErrorMessage(e) }); }
    finally { setLoading(false); }
  };

  useEffect(() => { carregar(); }, []);

  const salvar = async () => {
    setSaving(true);
    try {
      await apiClient.put('/api/open-finance/config', {
        clientId: form.clientId,
        clientSecret: form.clientSecret || undefined,   // vazio = mantém o atual
        webhookUrl: form.webhookUrl || undefined,
        ambiente: Number(form.ambiente),
        ativo: form.ativo,
      });
      toast.success('Configuração salva!');
      setForm(f => ({ ...f, clientSecret: '' }));
      carregar();
      onSalvo();
    } catch (e) { toast.error('Erro ao salvar', { description: getApiErrorMessage(e) }); }
    finally { setSaving(false); }
  };

  return (
    <div className="space-y-6">
      {loading && <div className="flex items-center gap-2 text-muted-foreground"><Loader2 className="h-4 w-4 animate-spin" />Carregando...</div>}

      <p className="text-sm text-muted-foreground">
        Informe as credenciais da sua aplicação Pluggy (Dashboard → API Keys). O <code>clientSecret</code> é
        cifrado em repouso e nunca é exibido de volta.
      </p>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="space-y-1">
          <Label>Client ID</Label>
          <Input value={form.clientId} onChange={e => setForm(f => ({ ...f, clientId: e.target.value }))} />
        </div>
        <div className="space-y-1">
          <Label>Client Secret</Label>
          <Input type="password"
            placeholder={segredoCadastrado ? '•••••• (já cadastrado — deixe vazio para manter)' : ''}
            value={form.clientSecret}
            onChange={e => setForm(f => ({ ...f, clientSecret: e.target.value }))} />
        </div>
        <div className="space-y-1 md:col-span-2">
          <Label>Webhook URL (HTTPS)</Label>
          <Input placeholder="https://api.suaempresa.com/api/open-finance-webhook"
            value={form.webhookUrl} onChange={e => setForm(f => ({ ...f, webhookUrl: e.target.value }))} />
          <p className="text-xs text-muted-foreground">
            Entregue à Pluggy nos tokens de conexão. Deve apontar para <code>/api/open-finance-webhook</code> da sua API.
          </p>
        </div>
        <div className="space-y-1">
          <Label>Ambiente</Label>
          <Select value={form.ambiente} onValueChange={v => setForm(f => ({ ...f, ambiente: v }))}>
            <SelectTrigger><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="1">Produção</SelectItem>
              <SelectItem value="0">Sandbox (conectores de teste)</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      <div className="flex items-center gap-2">
        <Checkbox id="ativo" checked={form.ativo} onCheckedChange={v => setForm(f => ({ ...f, ativo: Boolean(v) }))} />
        <Label htmlFor="ativo">Integração ativa</Label>
      </div>

      <Button onClick={salvar} disabled={saving}>
        {saving ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : null}Salvar
      </Button>
    </div>
  );
}

// ── Tab: Conexões ─────────────────────────────────────────────────────────

function TabConexoes({ config }: { config: ConfigOF | null }) {
  const [conexoes, setConexoes] = useState<ConexaoOF[]>([]);
  const [contasCC, setContasCC] = useState<ContaCorrente[]>([]);
  const [loading, setLoading] = useState(false);
  const [conectando, setConectando] = useState(false);
  const [syncId, setSyncId] = useState<number | null>(null);
  const [dtInicio, setDtInicio] = useState(() => {
    const d = new Date(); d.setDate(d.getDate() - 90); return d.toISOString().slice(0, 10);
  });
  const [dtFim, setDtFim] = useState(() => new Date().toISOString().slice(0, 10));

  const carregar = async () => {
    setLoading(true);
    try {
      const [cx, cc] = await Promise.all([
        apiClient.get<ConexaoOF[]>('/api/open-finance/conexoes'),
        apiClient.get<ContaCorrente[]>('/api/contas-correntes'),
      ]);
      setConexoes(cx.data);
      setContasCC(cc.data);
    } catch (e) { toast.error('Erro ao carregar', { description: getApiErrorMessage(e) }); }
    finally { setLoading(false); }
  };

  useEffect(() => { carregar(); }, []);

  // Abre o widget Pluggy Connect. updateItemId presente = renovação da conexão.
  const conectar = async (updateItemId?: string) => {
    if (!config?.clientId) { toast.error('Configure as credenciais da Pluggy primeiro.'); return; }
    setConectando(true);
    try {
      const { data } = await apiClient.post<{ accessToken: string }>(
        '/api/open-finance/connect-token', updateItemId ? { itemId: updateItemId } : {});

      const PluggyConnect = await carregarPluggyConnect();
      const widget = new PluggyConnect({
        connectToken: data.accessToken,
        includeSandbox: config.ambiente !== 1,
        updateItem: updateItemId,
        onSuccess: async ({ item }) => {
          try {
            await apiClient.post('/api/open-finance/conexoes/sincronizar', { itemId: item.id });
            toast.success('Banco conectado!');
            carregar();
          } catch (e) { toast.error('Erro ao registrar conexão', { description: getApiErrorMessage(e) }); }
        },
        onError: (err) => { console.error('Pluggy onError', err); },
      });
      widget.init();
    } catch (e) {
      toast.error('Não foi possível abrir o widget', { description: getApiErrorMessage(e) });
    } finally { setConectando(false); }
  };

  const sincronizarConexao = async (itemId: string) => {
    try {
      await apiClient.post('/api/open-finance/conexoes/sincronizar', { itemId });
      toast.success('Conexão atualizada!');
      carregar();
    } catch (e) { toast.error('Erro', { description: getApiErrorMessage(e) }); }
  };

  const vincular = async (contaId: number, contaCorrenteId: number | null) => {
    try {
      await apiClient.post(`/api/open-finance/contas/${contaId}/vincular`, { contaCorrenteId });
      toast.success(contaCorrenteId ? 'Conta vinculada!' : 'Vínculo removido.');
      carregar();
    } catch (e) { toast.error('Erro ao vincular', { description: getApiErrorMessage(e) }); }
  };

  const sincronizarExtrato = async (contaId: number) => {
    setSyncId(contaId);
    try {
      const { data } = await apiClient.post<{ importados: number; duplicados: number; lidos: number }>(
        `/api/open-finance/contas/${contaId}/extrato`, { dataInicio: dtInicio, dataFim: dtFim });
      toast.success(`Extrato sincronizado: ${data.importados} novo(s), ${data.duplicados} já existia(m).`);
    } catch (e) { toast.error('Erro ao sincronizar extrato', { description: getApiErrorMessage(e) }); }
    finally { setSyncId(null); }
  };

  const excluir = async (id: number) => {
    if (!confirm('Remover esta conexão? As transações já importadas serão mantidas.')) return;
    try {
      await apiClient.delete(`/api/open-finance/conexoes/${id}`);
      toast.success('Conexão removida.');
      carregar();
    } catch (e) { toast.error('Erro ao remover', { description: getApiErrorMessage(e) }); }
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-end gap-2">
        <Button onClick={() => conectar()} disabled={conectando} className="gap-1">
          {conectando ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plug className="h-4 w-4" />}
          Conectar instituição
        </Button>
        <Button variant="outline" size="sm" onClick={carregar} disabled={loading} className="gap-1">
          <RefreshCw className="h-3 w-3" />Atualizar
        </Button>

        <div className="ml-auto flex items-end gap-2">
          <div className="space-y-1">
            <Label className="text-xs">Extrato de</Label>
            <Input type="date" value={dtInicio} onChange={e => setDtInicio(e.target.value)} className="w-36" />
          </div>
          <div className="space-y-1">
            <Label className="text-xs">até</Label>
            <Input type="date" value={dtFim} onChange={e => setDtFim(e.target.value)} className="w-36" />
          </div>
        </div>
      </div>

      {!loading && conexoes.length === 0 && (
        <Card><CardContent className="py-10 text-center text-muted-foreground">
          Nenhuma instituição conectada. Clique em <strong>Conectar instituição</strong> para começar.
        </CardContent></Card>
      )}

      {conexoes.map(cx => (
        <Card key={cx.id}>
          <CardHeader className="flex flex-row items-center justify-between gap-2 space-y-0">
            <div className="flex items-center gap-3">
              <div className="h-9 w-9 rounded-lg bg-emerald-100 flex items-center justify-center">
                <Landmark className="h-5 w-5 text-emerald-700" />
              </div>
              <div>
                <CardTitle className="text-base">{cx.instituicao ?? `Item ${cx.itemId.slice(0, 8)}`}</CardTitle>
                <div className="flex items-center gap-2 mt-1">
                  {statusBadge(cx.status)}
                  {cx.consentExpiraEm && (
                    <span className="text-xs text-muted-foreground flex items-center gap-1">
                      <Clock className="h-3 w-3" />Consentimento até {new Date(cx.consentExpiraEm).toLocaleDateString('pt-BR')}
                    </span>
                  )}
                </div>
              </div>
            </div>
            <div className="flex gap-1">
              <Button size="sm" variant="outline" className="gap-1" onClick={() => sincronizarConexao(cx.itemId)}>
                <RefreshCw className="h-3 w-3" />Atualizar dados
              </Button>
              <Button size="sm" variant="outline" onClick={() => conectar(cx.itemId)}>Renovar consentimento</Button>
              <Button size="sm" variant="ghost" className="text-destructive" onClick={() => excluir(cx.id)}>
                <Trash2 className="h-4 w-4" />
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            {cx.contas.length === 0 ? (
              <p className="text-sm text-muted-foreground">Nenhuma conta descoberta neste item.</p>
            ) : (
              <div className="rounded-lg border overflow-hidden">
                <table className="w-full text-sm">
                  <thead className="bg-muted/50">
                    <tr>
                      {['Conta', 'Tipo', 'Saldo', 'Conta corrente (Udisys)', 'Extrato'].map(h => (
                        <th key={h} className="text-left px-3 py-2 font-medium text-muted-foreground">{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {cx.contas.map(conta => (
                      <tr key={conta.id} className="border-t hover:bg-muted/30">
                        <td className="px-3 py-2">
                          <div className="font-medium">{conta.nome ?? conta.numero ?? conta.accountId.slice(0, 8)}</div>
                          {conta.numero && <div className="text-xs text-muted-foreground">{conta.numero}</div>}
                        </td>
                        <td className="px-3 py-2">{conta.subtipo ?? conta.tipo ?? '—'}</td>
                        <td className="px-3 py-2 font-medium">{fmt(conta.saldo)}</td>
                        <td className="px-3 py-2">
                          <div className="flex items-center gap-1">
                            <Link2 className="h-3 w-3 text-muted-foreground" />
                            <Select
                              value={conta.contaCorrenteId ? String(conta.contaCorrenteId) : 'none'}
                              onValueChange={v => vincular(conta.id, v === 'none' ? null : Number(v))}>
                              <SelectTrigger className="w-56 h-8"><SelectValue placeholder="Vincular..." /></SelectTrigger>
                              <SelectContent>
                                <SelectItem value="none">— Não vinculada —</SelectItem>
                                {contasCC.map(cc => (
                                  <SelectItem key={cc.id} value={String(cc.id)}>{cc.descricao}</SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </div>
                        </td>
                        <td className="px-3 py-2">
                          <Button size="sm" variant="ghost" className="h-7 gap-1"
                            disabled={!conta.contaCorrenteId || syncId === conta.id}
                            onClick={() => sincronizarExtrato(conta.id)}>
                            {syncId === conta.id ? <Loader2 className="h-3 w-3 animate-spin" /> : <Download className="h-3 w-3" />}
                            Sincronizar
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>
      ))}
    </div>
  );
}

// ── Página principal ──────────────────────────────────────────────────────

const OpenFinance = () => {
  const [config, setConfig] = useState<ConfigOF | null>(null);

  const carregarConfig = async () => {
    try {
      const { data } = await apiClient.get<ConfigOF>('/api/open-finance/config');
      setConfig(data);
    } catch { /* silencioso — a aba Config mostra o erro */ }
  };

  useEffect(() => { carregarConfig(); }, []);

  return (
    <div className="p-6 space-y-6">
      <div className="flex items-center gap-3">
        <div className="h-10 w-10 rounded-lg bg-emerald-500 flex items-center justify-center">
          <Landmark className="h-5 w-5 text-white" />
        </div>
        <div>
          <h1 className="text-3xl font-bold text-foreground">Open Finance</h1>
          <p className="text-muted-foreground">Conecte os bancos do cliente (via Pluggy) e traga o extrato para a conciliação</p>
        </div>
        {config && (
          <div className="ml-auto">
            {config.segredoCadastrado
              ? <Badge className="bg-green-100 text-green-800 border-green-200 gap-1"><CheckCircle2 className="h-3 w-3" />Credenciais configuradas</Badge>
              : <Badge variant="destructive" className="gap-1"><XCircle className="h-3 w-3" />Sem credenciais</Badge>}
          </div>
        )}
      </div>

      <Tabs defaultValue="conexoes">
        <TabsList>
          <TabsTrigger value="conexoes" className="gap-1"><Plug className="h-4 w-4" />Conexões</TabsTrigger>
          <TabsTrigger value="config" className="gap-1"><Settings className="h-4 w-4" />Configuração</TabsTrigger>
        </TabsList>

        <TabsContent value="conexoes">
          <Card><CardContent className="pt-6"><TabConexoes config={config} /></CardContent></Card>
        </TabsContent>
        <TabsContent value="config">
          <Card><CardContent className="pt-6"><TabConfig onSalvo={carregarConfig} /></CardContent></Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default OpenFinance;
